import { Injectable } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Status } from '@df/core/status';
import { Klarna } from '@df/module-klarna/klarna.namespace';
import { IAddressBasicData } from '@df/session/api/address-book/address';
import { AddressService } from '@df/session/api/address-book/address.service';

@Injectable({ providedIn: 'root' })
export class KlarnaService {
    generalErrorMessage = Checkout.GENERAL_PAYMENT_ERROR_MESSAGE;

    constructor(
        protected request: RequestService,
        protected addressService: AddressService
    ) {}
    /**
     * Returns klarna payments api
     * @returns {Klarna.IPayments | undefined}
     */
    getPayments(): Klarna.IPayments | undefined {
        return typeof window === 'undefined' ? undefined : window.Klarna?.Payments;
    }

    /**
     * Sends klarna config request
     * @param {Klarna.Methods} method
     * @param {Status} status
     * @returns {Promise<Klarna.IConfig>}
     */
    loadConfig(method: Klarna.Methods, status?: Status): Promise<{ klarna: Klarna.IConfig }> {
        return this.request.send(status, { path: Klarna.Endpoints.CONFIG, data: { payment_method: method } });
    }

    /**
     * Converts basic address data to klarna payments address
     * @param {IAddressBasicData} address
     * @param {string} email
     * @returns {Klarna.IPaymentsAddress}
     */
    getAddress(address?: IAddressBasicData, email = ''): Klarna.IPaymentsAddress {
        return {
            email,
            given_name: address?.firstname || '',
            family_name: address?.lastname || '',
            street_address: address?.street_1 || '',
            street_address2: address?.street_2 || '',
            city: address?.city || '',
            postal_code: address?.postcode || '',
            country: address?.country_id || '',
            phone: address?.telephone || '',
            region: this.addressService.getRegionFromAddress(address)
        };
    }

    /**
     * Returns klarna payment method category
     * @param {Klarna.Methods} paymentMethodCode
     * @returns {string}
     */
    getPaymentMethodCategory(paymentMethodCode: Klarna.Methods): string {
        return paymentMethodCode.replace(/^klarna_/, '');
    }

    /**
     * Calls klarna payments authorize and finalize if required
     * @param {Klarna.IPaymentsFinalizeOptions}     finalizeOptions
     * @param {Klarna.IPaymentsAuthorizeData}       data
     * @param {Klarna.IPaymentsAuthorizeOptions}    authorizeOptions - if not provided finalizeOptions will be used,
     * required if we wants to split authorize and finalize steps
     * @returns {Promise<Klarna.IPaymentsAuthorizeResponse | Klarna.IPaymentsFinalizeResponse>}
     */
    authorize(
        finalizeOptions: Klarna.IPaymentsFinalizeOptions,
        data: Klarna.IData,
        authorizeOptions?: Klarna.IPaymentsAuthorizeOptions
    ): Promise<Klarna.IPaymentsAuthorizeResponse | Klarna.IPaymentsFinalizeResponse> {
        return new Promise((resolve, reject) => {
            const payments = this.getPayments();
            if (payments) {
                try {
                    payments.authorize(authorizeOptions || finalizeOptions, data, response => {
                        if (response.approved) {
                            if (response.finalize_required) {
                                this.finalize(finalizeOptions, data).then(resolve).catch(reject);
                            } else {
                                resolve(response);
                            }
                        } else {
                            reject(this.getError(response));
                        }
                    });
                } catch (e) {
                    reject(this.getError(e as any));
                }
            } else {
                reject(this.getError());
            }
        });
    }

    /**
     * Calls klarna payments finalize
     * @param {Klarna.IPaymentsFinalizeOptions} finalizeOptions
     * @param {Klarna.IPaymentsAuthorizeData} data
     * @returns {Klarna.IPaymentsFinalizeResponse}
     */
    finalize(finalizeOptions: Klarna.IPaymentsFinalizeOptions, data: Klarna.IData): Promise<Klarna.IPaymentsFinalizeResponse> {
        return new Promise((resolve, reject) => {
            const payments = this.getPayments();
            if (payments) {
                payments.finalize(finalizeOptions, data, response => {
                    if (response.approved) {
                        resolve(response);
                    } else {
                        reject(this.getError(response));
                    }
                });
            } else {
                reject(this.getError());
            }
        });
    }

    /**
     * Normalizes klarna error
     * @param {Klarna.IPaymentsAuthorizeResponse | Klarna.IPaymentsFinalizeResponse} response
     * @returns {string}
     */
    getError(response?: Klarna.IPaymentsFinalizeResponse | Klarna.IPaymentsAuthorizeResponse): string {
        const error = response?.error;

        if (error?.invalid_fields) {
            return `Invalid fields: ${error.invalid_fields.join(', ')}.`;
        }
        return this.generalErrorMessage;
    }
}
